import type { RemoteService } from '../../api/console-client'
import type { LocalState } from '../../operations/pull'
import { localState, pull } from '../../operations/pull'
import { describeWorkspace } from '../../project/auth'
import { plural } from '../../utils/format'
import { cliCommand } from '../../utils/invocation'
import { fail } from '../fail'
import { interactiveLogin } from '../interactive-login'
import { say, section } from '../output'
import { canRenderList, selectFromList } from '../prompts/select-list'
import { reportPull } from '../reporter'

export interface PullCommandOptions {
  /** Repeatable `-s`. */
  service?: string[]
  all?: boolean
  force?: boolean
}

export async function pullCommand(options: PullCommandOptions = {}): Promise<void> {
  say('Pulling services from the workspace...')

  try {
    const result = await pull({
      serviceIds         : options.service,
      all                : options.all,
      force              : options.force,
      onEvent            : reportPull,
      onAuthRequired     : interactiveLogin,
      onSelectionRequired: canRenderList() ? pickServices : undefined,
    })

    const where = describeWorkspace(result.workspace, { short: true })

    if (result.services.length === 0) {
      // "Nothing to do" and "nothing was written" are different answers, and only one of them is a shrug.
      section(result.upToDate.length > 0 && result.skipped.length === 0
        ? `  Everything is already up to date with ${ where }.`
        : `  Nothing pulled from ${ where }.`)
      say('')

      return
    }

    const names = result.services.map(service => service.serviceId).join(', ')

    section(`  Pulled ${ plural(result.services.length, 'service') } (${ names }) from ${ where }.`)
    say(`  Edit them under services/, then deploy them back with \`${ cliCommand() } deploy\`.`)
    say('')
  } catch (error) {
    fail(error)
  }
}

/** What each service's local copy is, phrased for the moment of choosing. */
const LOCAL_STATE_WORDING: Record<LocalState, string> = {
  absent   : ' — not here yet',
  same     : ' — up to date',
  different: ' — differs, needs --force',
}

async function pickServices(available: RemoteService[]): Promise<{ serviceIds?: string[]; all?: boolean }> {
  // Computed before the question rather than after the answer: whether a service is already up to date is
  // exactly what decides which one you want, so it belongs on the line you are choosing.
  const states = await Promise.all(available.map(service => localState(process.cwd(), service)))

  const choices = [
    { label: `All services [${ available.map(service => service.serviceId).join(', ') }]` },
    ...available.map((service, index) => ({
      label: `${ service.serviceId } (${ service.serviceName })${ LOCAL_STATE_WORDING[states[index]] }`,
    })),
  ]

  const index = await selectFromList('Which services should be pulled?', choices)

  return index === 0
    ? { all: true }
    : { serviceIds: [available[index - 1].serviceId] }
}
